import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useLocale } from '@/i18n/useLocale'
import { localePath, ROUTES } from '@/lib/routes'
import { ThemeToggle } from '@/theme/ThemeToggle'
import { SOFT_LAUNCH } from '@/lib/launchMode'

const PLAYGROUND_DOCS_URL = 'https://ode-playground.fly.dev/docs'

const navLinkClass =
  'rounded pr-2 py-1 text-base uppercase tracking-widest font-semibold marker-dot'

const navCtaClass =
  'hidden sm:inline-flex rounded-md pr-5 py-2 text-base uppercase tracking-widest font-semibold bg-mono-800 text-mono-100 dark:bg-mono-100 dark:text-mono-900 marker-dot ml-1'

export function Nav() {
  const { t } = useTranslation()
  const locale = useLocale()
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header
      className={`sticky top-0 z-40 transition-colors duration-200 ${
        scrolled ? 'bg-mono-100/85 dark:bg-mono-950/85 backdrop-blur' : ''
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <Link to={localePath(locale, ROUTES.home)} className="font-bold tracking-wider text-lg uppercase">
          {t('site.name')}
        </Link>

        <ul className="hidden md:flex items-center gap-6 opacity-85">
          <li>
            <a href="#features" className={navLinkClass}>{t('nav.features')}</a>
          </li>
          {!SOFT_LAUNCH && (
            <>
              <li>
                <Link to={localePath(locale, ROUTES.news)} className={navLinkClass}>{t('nav.news')}</Link>
              </li>
              <li>
                <a href={PLAYGROUND_DOCS_URL} target="_blank" rel="noreferrer" className={navLinkClass}>
                  {t('nav.docs')} ↗
                </a>
              </li>
            </>
          )}
        </ul>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <a
            href="#early-access"
            className={navCtaClass}
            onClick={() => setOpen(false)}
          >
            {t('nav.cta')}
          </a>
          <button
            type="button"
            aria-label="Open menu"
            aria-expanded={open}
            className="md:hidden p-2"
            onClick={() => setOpen((o) => !o)}
          >
            <span className="block w-5 h-px bg-current" />
            <span className="block w-5 h-px bg-current mt-1" />
            <span className="block w-5 h-px bg-current mt-1" />
          </button>
        </div>
      </nav>

      {open && (
        <div className="md:hidden border-t border-mono-300 dark:border-mono-800 px-5 py-4 space-y-2">
          <a href="#features" className="block py-2 text-xl uppercase tracking-widest font-semibold" onClick={() => setOpen(false)}>
            {t('nav.features')}
          </a>
          {!SOFT_LAUNCH && (
            <>
              <Link
                to={localePath(locale, ROUTES.news)}
                className="block py-2 text-xl uppercase tracking-widest font-semibold"
                onClick={() => setOpen(false)}
              >
                {t('nav.news')}
              </Link>
              <a href={PLAYGROUND_DOCS_URL} target="_blank" rel="noreferrer" className="block py-2 text-xl uppercase tracking-widest font-semibold">
                {t('nav.docs')} ↗
              </a>
            </>
          )}
          <a
            href="#early-access"
            className="block px-5 py-3 rounded-md text-center text-xl uppercase tracking-widest font-semibold bg-mono-800 text-mono-100 dark:bg-mono-100 dark:text-mono-900 mt-3"
            onClick={() => setOpen(false)}
          >
            {t('nav.cta')}
          </a>
        </div>
      )}
    </header>
  )
}
